import axios from 'axios';
import backend_url from '../config/config';
import enhancedCacheService from './enhancedCacheService';

// Create axios instance with optimized config
const api = axios.create({
  baseURL: backend_url,
  timeout: 30000, // Reduced timeout for faster failure detection
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request deduplication map
const pendingRequests = new Map();

// Add request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor with caching
api.interceptors.response.use(
  (response) => {
    // Cache successful responses based on URL and params
    const cacheKey = generateCacheKey(response.config);
    if (shouldCache(response.config)) {
      enhancedCacheService.set(cacheKey, response.data, getCacheTTL(response.config));
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 429 rate limiting with exponential backoff
    if (error.response?.status === 429 && !originalRequest._retry) {
      originalRequest._retry = true;
      const retryDelay = Math.min(1000 * Math.pow(2, originalRequest._retryCount || 0), 10000);
      
      await new Promise(resolve => setTimeout(resolve, retryDelay));
      return api(originalRequest);
    }

    // Handle 401 errors
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

// Generate cache key from request config
function generateCacheKey(config) {
  const { url, method, params, data } = config;
  const key = `${method}:${url}:${JSON.stringify(params || {})}:${JSON.stringify(data || {})}`;
  return btoa(key).replace(/[^a-zA-Z0-9]/g, '');
}

// Determine if response should be cached
function shouldCache(config) {
  const { method, url } = config;
  return method === 'get' && (
    url.includes('/orders') ||
    url.includes('/items') ||
    url.includes('/categories') ||
    url.includes('/branches')
  );
}

// Get cache TTL based on endpoint
function getCacheTTL(config) {
  const { url } = config;
  if (url.includes('/orders')) return 2 * 60 * 1000; // 2 minutes for orders
  if (url.includes('/items')) return 10 * 60 * 1000; // 10 minutes for items
  if (url.includes('/categories')) return 30 * 60 * 1000; // 30 minutes for categories
  return 5 * 60 * 1000; // 5 minutes default
}

// Deduplicate requests
async function deduplicateRequest(config) {
  const requestKey = generateCacheKey(config);
  
  // Check if request is already pending
  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey);
  }
  
  // Check cache first
  const cached = enhancedCacheService.get(requestKey);
  if (cached) {
    return { data: cached };
  }
  
  // Make new request
  const requestPromise = api(config);
  pendingRequests.set(requestKey, requestPromise);
  
  try {
    const response = await requestPromise;
    return response;
  } finally {
    pendingRequests.delete(requestKey);
  }
}

// Optimized API service
const optimizedApiService = {
  // Orders API with smart caching and pagination
  orders: {
    async getAll(options = {}) {
      const { limit = 50, page = 1, status, forceRefresh = false } = options;
      
      if (!forceRefresh) {
        const cacheKey = `orders:${limit}:${page}:${status || 'all'}`;
        const cached = enhancedCacheService.get(cacheKey);
        if (cached) {
          return { data: cached };
        }
      }
      
      const config = {
        method: 'get',
        url: '/orders',
        params: { limit, page, status }
      };
      
      return deduplicateRequest(config);
    },

    async getById(id) {
      const config = {
        method: 'get',
        url: `/orders/${id}`
      };
      return deduplicateRequest(config);
    },

    async update(id, data) {
      const response = await api.patch(`/orders/${id}`, data);
      // Invalidate related caches
      enhancedCacheService.invalidateOrders();
      return response;
    },

    async create(data) {
      const response = await api.post('/orders', data);
      // Invalidate related caches
      enhancedCacheService.invalidateOrders();
      return response;
    }
  },

  // Items API with caching
  items: {
    async getAll(options = {}) {
      const { limit = 100, page = 1, category, section } = options;
      
      const cacheKey = `items:${limit}:${page}:${category || 'all'}:${section || 'all'}`;
      const cached = enhancedCacheService.get(cacheKey);
      if (cached) {
        return { data: cached };
      }
      
      const config = {
        method: 'get',
        url: '/items',
        params: { limit, page, category, section }
      };
      
      return deduplicateRequest(config);
    }
  },

  // Categories API with long-term caching
  categories: {
    async getAll() {
      const cacheKey = 'categories:all';
      const cached = enhancedCacheService.get(cacheKey);
      if (cached) {
        return { data: cached };
      }
      
      const config = {
        method: 'get',
        url: '/categories'
      };
      
      return deduplicateRequest(config);
    }
  },

  // Branches API with long-term caching
  branches: {
    async getAll() {
      const cacheKey = 'branches:all';
      const cached = enhancedCacheService.get(cacheKey);
      if (cached) {
        return { data: cached };
      }
      
      const config = {
        method: 'get',
        url: '/branches'
      };
      
      return deduplicateRequest(config);
    }
  },

  // Utility methods
  clearCache() {
    enhancedCacheService.clear();
  },

  getCacheStats() {
    return enhancedCacheService.getStats();
  }
};

export default optimizedApiService;
